// AdminNotifyPanel.jsx — Settings panel for admin to send a notification.
// Two modes:
//  • Single user — appears in that user's HomePage inbox.
//  • Broadcast — fan-out to every active user (optionally filtered by role).
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";

const PRIORITIES = [
  { id: "info",    label: "ℹ إعلام",   color: "#0F766E" },
  { id: "warning", label: "⚠ تنبيه",   color: "#D97706" },
  { id: "urgent",  label: "🚨 عاجل",    color: "#B91C1C" },
];

const ROLES = [
  { id: "",                  label: "كل الموظفين" },
  { id: "admin",             label: "👑 الإدارة" },
  { id: "operations",        label: "⚙ العمليات" },
  { id: "marketing",         label: "🛍 التسويق" },
  { id: "branch_manager",    label: "🏪 مديري الفروع" },
  { id: "viewer",            label: "👁 المشاهدون" },
];

const TEMPLATES = [
  {
    id: "meeting",
    label: "📅 تذكير اجتماع",
    priority: "info",
    title: "تذكير: اجتماع اليوم",
    body: "تذكير بحضور الاجتماع الساعة ___ في ___\n\nالمواضيع:\n• \n• \n\nيرجى الحضور في الوقت المحدد.",
  },
  {
    id: "task",
    label: "🎯 تكليف عاجل",
    priority: "urgent",
    title: "تكليف عاجل",
    body: "تم تكليفك بالمهمة التالية:\n\nالمهمة: ___\nالموعد النهائي: ___\nالأولوية: عالية\n\nيرجى التنفيذ والإفادة.",
  },
  {
    id: "kudos",
    label: "🎉 تهنئة",
    priority: "info",
    title: "أحسنت! 🌟",
    body: "أهنّئك على أدائك المميز في ___\n\nاستمر بهذه الروح — أنت مثال يُحتذى به للفريق.",
  },
  {
    id: "report",
    label: "📊 طلب تقرير",
    priority: "warning",
    title: "طلب تقرير",
    body: "أحتاج تقريراً عن ___ بحلول ___\n\nيجب أن يشمل:\n• \n• \n\nشكراً لتعاونك.",
  },
  {
    id: "alert",
    label: "⚠ تنبيه",
    priority: "warning",
    title: "تنبيه مهم",
    body: "ألفت انتباهك إلى ___\n\nالإجراء المطلوب: ___\n\nيرجى المتابعة في أقرب وقت.",
  },
];

const BROADCAST_TEMPLATES = [
  {
    id: "broadcast-urgent",
    label: "🚨 تنبيه عاجل لكل الفريق",
    priority: "urgent",
    title: "🚨 تنبيه عاجل",
    body: "إلى جميع الفريق:\n\n___\n\nيرجى التعامل فوراً.",
  },
  {
    id: "broadcast-meeting",
    label: "📣 اجتماع عام",
    priority: "warning",
    title: "📣 اجتماع عام للفريق",
    body: "اجتماع للفريق كاملاً:\n\nالموعد: ___\nالمكان: ___\nالموضوع: ___\n\nالحضور إلزامي.",
  },
  {
    id: "broadcast-update",
    label: "📢 إعلان عام",
    priority: "info",
    title: "📢 إعلان من الإدارة",
    body: "إلى الفريق الكريم:\n\n___\n\nتحياتي.",
  },
];

export default function AdminNotifyPanel() {
  const [mode, setMode] = useState("single"); // 'single' | 'broadcast'
  const [users, setUsers] = useState([]);
  const [toUser, setToUser] = useState("");
  const [bcastRole, setBcastRole] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("info");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    api.get("/auth/users/list").then(({ data }) => {
      setUsers(data.items || []);
    }).catch((e) => {
      console.warn("[AdminNotifyPanel] failed to load users:", e?.response?.status, e?.message);
    });
  }, []);

  // Active templates depend on mode
  const templates = mode === "broadcast" ? BROADCAST_TEMPLATES : TEMPLATES;

  // Recipient count preview for broadcast
  const broadcastCount = mode === "broadcast"
    ? users.filter((u) => !bcastRole || u.role === bcastRole).length
    : 0;

  const clear = () => {
    setTitle(""); setBody(""); setPriority(mode === "broadcast" ? "urgent" : "info");
    setConfirming(false); setMsg(null);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setPriority(newMode === "broadcast" ? "urgent" : "info");
    setMsg(null); setConfirming(false);
  };

  const sendSingle = async () => {
    if (!toUser || !title.trim() || !body.trim()) {
      setMsg({ t: "r", m: "اختر المستلم واكتب العنوان والنص" });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      await api.post("/notifications/send", {
        to_username: toUser,
        title: title.trim(),
        body: body.trim(),
        priority,
      });
      setMsg({ t: "g", m: `✅ تم إرسال الإشعار إلى ${toUser}` });
      setTitle(""); setBody(""); setToUser("");
      setTimeout(() => setMsg(null), 4000);
    } catch (e) {
      setMsg({ t: "r", m: e?.response?.data?.detail || "تعذّر الإرسال" });
    } finally { setBusy(false); }
  };

  const sendBroadcast = async () => {
    if (!title.trim() || !body.trim()) {
      setMsg({ t: "r", m: "اكتب العنوان والنص" });
      return;
    }
    if (!confirming) { setConfirming(true); return; }
    setBusy(true); setMsg(null); setConfirming(false);
    try {
      const { data } = await api.post("/notifications/broadcast", {
        title: title.trim(),
        body: body.trim(),
        priority,
        role: bcastRole || null,
      });
      const recip = data?.recipients ?? data?.sent ?? 0;
      const push = data?.push_sent ?? 0;
      setMsg({ t: "g", m: `✅ أُرسلت إلى ${recip} موظف · 🔔 push إلى ${push}` });
      setTitle(""); setBody("");
      setTimeout(() => setMsg(null), 6000);
    } catch (e) {
      setMsg({ t: "r", m: e?.response?.data?.detail || "تعذّر الإرسال" });
    } finally { setBusy(false); }
  };

  const send = mode === "broadcast" ? sendBroadcast : sendSingle;

  return (
    <div className="card" data-testid="admin-notify-panel" style={{ marginBottom: 14, padding: 16 }}>
      <h4 style={{ margin: "0 0 4px", color: "var(--brown-deep)", fontSize: 14 }}>📨 إشعارات الموظفين</h4>
      <p style={{ margin: "0 0 12px", fontSize: 11.5, color: "var(--muted)", lineHeight: 1.7 }}>
        الإشعار يظهر فوراً في الصفحة الرئيسية للمستلم + Web Push على جهازه. الوضع «بثّ عام» يصل لكل الموظفين دفعةً واحدة.
      </p>

      {/* Mode switcher */}
      <div data-testid="notify-mode" style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button type="button" data-testid="notify-mode-single"
          onClick={() => switchMode("single")}
          style={modeBtnStyle(mode === "single")}>
          👤 موظف واحد
        </button>
        <button type="button" data-testid="notify-mode-broadcast"
          onClick={() => switchMode("broadcast")}
          style={modeBtnStyle(mode === "broadcast", "#B91C1C")}>
          📣 بثّ عام لكل الموظفين
        </button>
      </div>

      {/* Template library */}
      <div data-testid="notify-templates" style={{ marginBottom: 12 }}>
        <label style={lblStyle}>قوالب جاهزة (ضغطة وعدّل):</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`notify-template-${t.id}`}
              onClick={() => {
                setTitle(t.title);
                setBody(t.body);
                setPriority(t.priority);
              }}
              style={{
                padding: "6px 12px", borderRadius: 18, cursor: "pointer",
                background: "var(--beige)", color: "var(--brown-deep)",
                border: "1px solid var(--border)", fontSize: 12, fontWeight: 700,
                fontFamily: "var(--f-n)", transition: "all 140ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--brown)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "var(--beige)"; e.currentTarget.style.color = "var(--brown-deep)"; }}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            data-testid="notify-template-clear"
            onClick={clear}
            style={{
              padding: "6px 12px", borderRadius: 18, cursor: "pointer",
              background: "transparent", color: "var(--muted)",
              border: "1px dashed var(--border)", fontSize: 11.5, fontWeight: 600,
              fontFamily: "var(--f-n)",
            }}
          >
            ✕ مسح
          </button>
        </div>
      </div>

      {mode === "single" ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={lblStyle}>المستلم:</label>
            <select
              value={toUser}
              onChange={(e) => setToUser(e.target.value)}
              data-testid="notify-to-user"
              style={inputStyle}
            >
              <option value="">— اختر موظف —</option>
              {users.map((u) => (
                <option key={u.username} value={u.username}>
                  {u.full_name || u.username} ({u.role})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={lblStyle}>الأولوية:</label>
            <PriorityPicker priority={priority} setPriority={setPriority} />
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={lblStyle}>الفئة المستهدفة:</label>
            <select
              value={bcastRole}
              onChange={(e) => setBcastRole(e.target.value)}
              data-testid="notify-broadcast-role"
              style={inputStyle}
            >
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}
              data-testid="notify-broadcast-count">
              👥 سيتلقّى الرسالة: <b>{broadcastCount}</b> موظف
            </div>
          </div>
          <div>
            <label style={lblStyle}>الأولوية:</label>
            <PriorityPicker priority={priority} setPriority={setPriority} />
          </div>
        </div>
      )}

      <label style={lblStyle}>العنوان:</label>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={mode === "broadcast" ? "مثال: 🚨 تنبيه عاجل لجميع الفريق" : "مثال: تذكير بحضور الاجتماع"}
        data-testid="notify-title"
        style={inputStyle}
      />

      <label style={lblStyle}>النص:</label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="اكتب الرسالة كاملة هنا..."
        rows={3}
        data-testid="notify-body"
        style={{ ...inputStyle, resize: "vertical", fontFamily: "var(--f-n)" }}
      />

      {mode === "broadcast" && confirming && (
        <div data-testid="notify-broadcast-confirm" style={{
          marginTop: 10, padding: 12, background: "rgba(185,28,28,0.08)",
          border: "1px solid rgba(185,28,28,0.3)", borderRadius: 8,
        }}>
          <div style={{ fontSize: 12.5, color: "#B91C1C", fontWeight: 700, marginBottom: 6 }}>
            ⚠ تأكيد: ستُرسل هذه الرسالة فوراً إلى <b>{broadcastCount}</b> موظف.
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={() => setConfirming(false)}
              style={{ padding: "5px 14px", borderRadius: 6, cursor: "pointer", background: "transparent",
                color: "var(--muted)", border: "1px solid var(--border)", fontSize: 12, fontWeight: 600 }}>
              إلغاء
            </button>
            <button type="button" onClick={sendBroadcast} disabled={busy}
              data-testid="notify-broadcast-confirm-btn"
              style={{ padding: "5px 14px", borderRadius: 6, cursor: "pointer", background: "#B91C1C",
                color: "#fff", border: "none", fontSize: 12, fontWeight: 700 }}>
              {busy ? "⏳ يُرسل..." : "✅ نعم، أرسل الآن"}
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={send}
        disabled={busy}
        data-testid={mode === "broadcast" ? "notify-broadcast-btn" : "notify-send-btn"}
        className={mode === "broadcast" ? "btn" : "btn btn-success"}
        style={{
          marginTop: 10, padding: "8px 20px", fontSize: 12.5,
          ...(mode === "broadcast"
            ? { background: confirming ? "var(--muted)" : "#B91C1C", color: "#fff", border: "none" }
            : {}),
        }}
      >
        {busy ? "⏳ يُرسل..." : mode === "broadcast"
          ? (confirming ? "↑ اضغط للتأكيد أعلاه" : `🚨 إرسال بثّ عاجل (${broadcastCount})`)
          : "📤 إرسال الإشعار"}
      </button>

      {msg && (
        <div data-testid="notify-result"
          style={{ marginTop: 10, fontSize: 12, color: msg.t === "g" ? "var(--success)" : "var(--danger)", fontWeight: 600 }}>
          {msg.m}
        </div>
      )}
    </div>
  );
}

function PriorityPicker({ priority, setPriority }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {PRIORITIES.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => setPriority(p.id)}
          data-testid={`notify-priority-${p.id}`}
          style={{
            flex: 1, padding: "8px 6px", borderRadius: 6, cursor: "pointer",
            background: priority === p.id ? p.color : "var(--beige)",
            color: priority === p.id ? "#fff" : "var(--brown-deep)",
            border: "1px solid var(--border)", fontSize: 11.5, fontWeight: 700,
          }}
        >{p.label}</button>
      ))}
    </div>
  );
}

const lblStyle = { display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--brown-deep)", marginTop: 10, marginBottom: 4 };
const inputStyle = {
  width: "100%", padding: "8px 10px", border: "1px solid var(--border)",
  borderRadius: 6, fontSize: 12.5, background: "#fff", fontFamily: "var(--f-n)",
};
function modeBtnStyle(active, activeColor = "var(--brown)") {
  return {
    flex: 1, padding: "8px 12px", borderRadius: 6, cursor: "pointer",
    background: active ? activeColor : "var(--beige)",
    color: active ? "#fff" : "var(--brown-deep)",
    border: "1px solid var(--border)", fontSize: 12, fontWeight: 700,
    fontFamily: "var(--f-n)", transition: "all 140ms ease",
  };
}
